<template>
  <QForm
    ref="contactForm"
    class="tw-w-full"
    @submit="onSubmit"
    @reset="onReset"
  >
    <div class="tw-flex tw-flex-col tw-gap-4">
      <div class="tw-basis-full">
        <InputText
          v-model="dataSet.fullName"
          :name="$t('components.contact.form.fullName')"
          :placeholder="$t('components.contact.form.fullName')"
          rules="required"
          :filled="filled"
          :outlined="outlined"
          :borderless="borderless"
          :rounded="rounded"
          :square="square"
          :dense="dense"
          :disable="disable || loading"
          :input-class="inputInnerClass"
          :label="props.labels.name"
          :container-class="inputContainerClass"
          :hide-bottom-space="hideBottomSpace"
          :error-messages="errorMessages"
        />
      </div>
      <div
        :class="[
          'tw-gap-4',
          splitContactFields
            ? 'tw-flex tw-flex-row'
            : 'tw-flex tw-flex-col',
        ]"
      >
        <div :class="splitContactFields ? 'tw-w-1/2' : 'tw-w-full'">
          <InputText
            v-model="dataSet.tel"
            :name="$t('components.contact.form.tel')"
            :placeholder="$t('components.contact.form.tel')"
            rules="required|phone"
            :filled="filled"
            :outlined="outlined"
            :borderless="borderless"
            :rounded="rounded"
            :square="square"
            :dense="dense"
            :disable="disable || loading"
            :input-class="inputInnerClass"
            :label="props.labels.tel"
            :container-class="inputContainerClass"
            :hide-bottom-space="hideBottomSpace"
            :error-messages="errorMessages"
          />
        </div>
        <div :class="splitContactFields ? 'tw-w-1/2' : 'tw-w-full'">
          <InputText
            v-model="dataSet.email"
            :name="$t('components.contact.form.email')"
            :placeholder="$t('components.contact.form.email')"
            rules="email"
            :filled="filled"
            :outlined="outlined"
            :borderless="borderless"
            :rounded="rounded"
            :square="square"
            :dense="dense"
            :disable="disable || loading"
            :input-class="inputInnerClass"
            :label="props.labels.email"
            :container-class="inputContainerClass"
            :hide-bottom-space="hideBottomSpace"
            :error-messages="errorMessages"
          />
        </div>
      </div>
      <div class="tw-basis-full">
        <InputText
          v-model="dataSet.subject"
          :name="$t('components.contact.form.subject')"
          :placeholder="$t('components.contact.form.subject')"
          :filled="filled"
          :outlined="outlined"
          :borderless="borderless"
          :rounded="rounded"
          :square="square"
          :dense="dense"
          :disable="disable || loading"
          :input-class="inputInnerClass"
          :label="props.labels.subject"
          :container-class="inputContainerClass"
          :hide-bottom-space="hideBottomSpace"
          :error-messages="errorMessages"
        />
      </div>
      <div class="tw-basis-full">
        <InputTextArea
          v-model="dataSet.message"
          :name="$t('components.contact.form.message')"
          :placeholder="$t('components.contact.form.message')"
          :rows="textareaRows"
          :counter="textareaCounter"
          :maxlength="textareaMaxlength"
          :filled="filled"
          :outlined="outlined"
          :borderless="borderless"
          :rounded="rounded"
          :square="square"
          :dense="dense"
          :disable="disable || loading"
          :input-class="inputInnerClass"
          :label="props.labels.detail"
        />
      </div>
      <div class="tw-w-full">
        <ButtonPrimary
          :label="$t('components.contact.form.button')"
          type="submit"
          block
          :class="[buttonClass, 'form-contact-submit', 'tw-text-mb-button lg:tw-text-dt-button']"
          :loading="loading"
          class="tw-w-full !tw-h-[48px] !tw-shadow-none"
          unelevated
        />
      </div>
    </div>
  </QForm>
</template>

<script setup lang="ts">
const props = defineProps({
  categoryEventName: {
    type: String,
    default: "",
  },
  isCentered: {
    type: Boolean,
    default: false,
  },
  // Style customization props
  buttonClass: {
    type: String,
    default: "tw-w-2/5",
  },
  submitButtonLabel: {
    type: String,
    default: "ส่งข้อความ",
  },
  // Quasar input props
  filled: {
    type: Boolean,
    default: true,
  },
  outlined: {
    type: Boolean,
    default: false,
  },
  borderless: {
    type: Boolean,
    default: false,
  },
  rounded: {
    type: Boolean,
    default: false,
  },
  square: {
    type: Boolean,
    default: false,
  },
  dense: {
    type: Boolean,
    default: false,
  },
  disable: {
    type: Boolean,
    default: false,
  },
  inputInnerClass: {
    type: String,
    default: "",
  },
  // Textarea specific props
  textareaRows: {
    type: String,
    default: "5",
  },
  textareaCounter: {
    type: Boolean,
    default: false,
  },
  textareaMaxlength: {
    type: String,
    default: "500",
  },
  labels: {
    type: Object as PropType<Record<string, string>>,
    default: () => ({}),
  },
  hideBottomSpace: {
    type: Boolean,
    default: false,
  },
  inputContainerClass: {
    type: String,
    default: "",
  },
  splitContactFields: {
    type: Boolean,
    default: true,
  },
  errorMessages: {
    type: Object as PropType<Record<string, string>>,
    default: () => ({}),
  },
});

const dataSet = reactive({
  fullName: "",
  tel: "",
  email: "",
  subject: "",
  message: "",
});

const contactForm = ref(null);
const { t } = useI18n();
const notify = useNotify();
const loading = ref(false);

// Analytics event
const { analyticEvent, fbPixelEvent } = useAnalytic();
const eventContactUsForm = () => {
  analyticEvent({
    ga4ActionName: "click_contact_submit",
    ga4Label: props.submitButtonLabel,
  });

  fbPixelEvent({
    fbEvent: "Contact",
  });
};

const { $api } = useNuxtApp();

const onSubmit = async () => {
  loading.value = true;
  try {
    await ($api as typeof $fetch)("/contact/send-email", {
      method: "POST",
      body: {
        ...dataSet,
      },
    });

    eventContactUsForm();
    notify.success(t("components.contact.successMessage"));
    onReset();
  } catch (error) {
    console.error(error);
    notify.error(t("global.somethingWentWrong"));
  } finally {
    loading.value = false;
  }
};

const onReset = () => {
  dataSet.fullName = "";
  dataSet.tel = "";
  dataSet.email = "";
  dataSet.subject = "";
  dataSet.message = "";
};
</script>

<style scoped>
:deep(.form-contact-submit.q-btn) {
  background: var(--theme-color-primary) !important;
  color: var(--theme-color-inverted-primary) !important;
}
</style>
